import React, { useEffect, useState } from 'react';
import { DoctoralProfileData } from '../types';
import { Card, Field, Hero, Kpis } from './dp120Parts';

interface DoctoralProfileViewProps {
  data: DoctoralProfileData | null;
  onSave?: (updated: DoctoralProfileData) => void | Promise<void>;
}

const empty = (data: DoctoralProfileData | null): DoctoralProfileData => ({ ...(data || {}) });

export const DoctoralProfileView: React.FC<DoctoralProfileViewProps> = ({ data, onSave }) => {
  const [form, setForm] = useState<DoctoralProfileData>(() => empty(data));
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setForm(empty(data));
  }, [data]);

  const set = (key: keyof DoctoralProfileData) => (value: string) => {
    const numeric = key === 'current_year' || key === 'current_semester';
    setForm((prev) => ({ ...prev, [key]: numeric ? Number(value) || null : value }));
  };
  const text = (key: keyof DoctoralProfileData) => String(form[key] ?? '');

  const save = async () => {
    if (!onSave) return;
    setNotice('');
    try {
      await onSave(form);
      setNotice('Профиль сохранён.');
    } catch {
      setNotice('Не удалось сохранить профиль.');
    }
  };

  return (
    <>
      <Hero
        kicker="PERSONAL RECORD"
        title="Профиль докторанта"
        text="Персональные, академические и научные идентификаторы. ФИО фиксируется на трёх языках по официальным документам."
        actions={
          <button type="button" className="dp120-btn gold" onClick={save}>
            Сохранить профиль
          </button>
        }
      />
      {notice ? <div className="dp120-callout" style={{ marginTop: 10 }}><b>{notice}</b></div> : null}
      <Kpis
        items={[
          ['Профиль', 'Основные данные', 'Личные, академические и научные сведения'],
          ['ORCID', form.orcid_id ? 'Указан' : 'Нужно заполнить', 'Отдельное обязательное поле'],
          ['Scopus ID', form.scopus_id ? 'Указан' : 'Не указан', 'Отдельный научный ID'],
          ['WoS ResearcherID', form.wos_id ? 'Указан' : 'Не указан', 'Отдельный научный ID'],
        ]}
      />
      <div className="dp120-grid">
        <Card title="ФИО по документу · 3 языка" sub="Пользователь подтверждает написание по документам." span="span12">
          <div className="dp120-form">
            <Field label="Тегі · қазақша" value={text('last_name_kz')} onChange={set('last_name_kz')} />
            <Field label="Аты · қазақша" value={text('first_name_kz')} onChange={set('first_name_kz')} />
            <Field label="Әкесінің аты · қазақша" value={text('patronymic_kz')} onChange={set('patronymic_kz')} />
            <Field label="Фамилия · русский" value={text('last_name_ru')} onChange={set('last_name_ru')} />
            <Field label="Имя · русский" value={text('first_name_ru')} onChange={set('first_name_ru')} />
            <Field label="Отчество · русский" value={text('patronymic_ru')} onChange={set('patronymic_ru')} />
            <Field label="Surname · English/Latin" value={text('last_name_en')} onChange={set('last_name_en')} />
            <Field label="Given name · English/Latin" value={text('first_name_en')} onChange={set('first_name_en')} />
            <Field label="Middle name / Patronymic" value={text('patronymic_en')} onChange={set('patronymic_en')} />
          </div>
        </Card>
        <Card title="Личные данные" sub="ИИН относится к защищаемым персональным данным.">
          <div className="dp120-form">
            <Field label="Дата рождения" type="date" value={text('birth_date')} onChange={set('birth_date')} />
            <Field label="Место рождения" value={text('birth_place')} onChange={set('birth_place')} />
            <Field label="ИИН" value={text('iin')} onChange={set('iin')} />
            <Field label="Адрес проживания" value={text('living_address')} onChange={set('living_address')} full />
            <Field label="Адрес регистрации" value={text('registration_address')} onChange={set('registration_address')} full />
          </div>
        </Card>
        <Card title="Докторантура и группа" sub="Курс и семестр берутся из учебного дела.">
          <div className="dp120-form">
            <Field label="Период поступления" value={text('admission_period')} onChange={set('admission_period')} />
            <Field label="Форма финансирования" value={text('funding_form')} onChange={set('funding_form')} />
            <Field label="Код программы" value={text('program_code')} onChange={set('program_code')} />
            <Field label="Образовательная программа" value={text('program_title')} onChange={set('program_title')} />
            <Field label="Название группы" value={text('group_name')} onChange={set('group_name')} />
            <Field label="Текущий курс" type="number" value={text('current_year')} onChange={set('current_year')} />
            <Field label="Текущий семестр" type="number" value={text('current_semester')} onChange={set('current_semester')} />
          </div>
        </Card>
        <Card title="Контакты и работа" sub="Один основной Email используется во всём кабинете.">
          <div className="dp120-form">
            <Field label="Основной Email" value={text('main_email')} onChange={set('main_email')} />
            <Field label="Телефон / WhatsApp" value={text('phone_whatsapp')} onChange={set('phone_whatsapp')} />
            <Field label="Текущее место работы и должность" value={text('workplace_position')} onChange={set('workplace_position')} full />
          </div>
        </Card>
        <Card title="Научные идентификаторы" sub="Отдельные поля профиля.">
          <div className="dp120-form">
            <Field label="ORCID iD" value={text('orcid_id')} onChange={set('orcid_id')} full placeholder="0000-0000-0000-0000" />
            <Field label="Scopus Author ID" value={text('scopus_id')} onChange={set('scopus_id')} full />
            <Field label="Web of Science ResearcherID" value={text('wos_id')} onChange={set('wos_id')} full />
          </div>
        </Card>
        <Card title="Предыдущее образование" sub="Данные бакалавриата и магистратуры." span="span12">
          <div className="dp120-form">
            <Field label="Бакалавриат" value={text('bachelor_info')} onChange={set('bachelor_info')} full />
            <Field label="Магистратура" value={text('master_info')} onChange={set('master_info')} full />
          </div>
        </Card>
      </div>
    </>
  );
};
